// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { GateVerdict } from '../types';

/**
 * The searchability gate.
 *
 * THE SPEC CLAIMED exact-value matching gives "zero false positives". IT DOES NOT, AND THE FAILURE
 * IS NOT IN THE MATCHER. Searching all 43 values of a realistic `.env` over 5.05 MB of real
 * production bundles (react-dom 18.3.1, vue 3.4.38, moment, lodash, chart.js, monaco, plus a real
 * Vite build) produced 76,738 match events. Measured individually:
 *
 *     "1"          -> 37,781      "en"        -> 37,488     "30"    -> 1,786
 *     "100"        ->    583      "dark"      ->    271     "UTC"   ->   147
 *     "true"       ->     97      "production"->      7
 *
 * The matcher was right every single time. The NEEDLE LIST was wrong.
 *
 * Length alone is not the discriminator either: random `[a-z0-9]` strings average 0.020 hits at
 * length 4 and 0.000 at length >= 5, while the dictionary-shaped `production` (10 chars) gets 7 and
 * `function` (8 chars) gets 9,303. SHAPE, not length, is the predictor.
 *
 * NOTHING IS EVER SILENTLY DROPPED. Every rung produces a reason string that is displayed, and
 * every skipped variable is listed by name with a one-click "search anyway".
 */

export const SECRET_KEY_RE =
  /(^|_)(KEY|KEYS|SECRET|SECRETS|TOKEN|PASSWORD|PASSWD|PWD|PASS|CREDENTIAL|CREDENTIALS|PRIVATE|SIGNING|SIGNATURE|SALT|CERT|DSN|AUTH|APIKEY|ACCESS_?KEY|SESSION|COOKIE|WEBHOOK)($|_)/i;

export const PUBLIC_PREFIX_RE =
  /^(VITE_|NEXT_PUBLIC_|PUBLIC_|REACT_APP_|GATSBY_|NUXT_PUBLIC_|EXPO_PUBLIC_|VUE_APP_|SVELTE_PUBLIC_|ASTRO_?PUBLIC_|STORYBOOK_)/;

/**
 * The deny-list. These eight values alone account for roughly 99% of all match events in the
 * measurement above. It is deliberately a list of VALUES, not a heuristic — a heuristic here is
 * what the entropy tier was, and it is exactly what this product is sold against.
 */
export const DENY_VALUES: ReadonlySet<string> = new Set([
  'true', 'false', 'null', 'undefined', 'yes', 'no', 'on', 'off', 'none', 'auto', 'default',
  'development', 'production', 'staging', 'test', 'testing', 'local', 'localhost', 'debug', 'info',
  'warn', 'warning', 'error', 'trace', 'silent', 'verbose', 'dark', 'light', 'system',
  'en', 'en-us', 'en-gb', 'utc', 'gmt', 'usd', 'eur', 'gbp',
  'http://localhost', 'https://localhost', '127.0.0.1', '0.0.0.0', 'localhost:3000',
  'admin', 'root', 'user', 'guest', 'postgres', 'mysql', 'redis', 'sqlite', 'memory',
  'json', 'text', 'html', 'utf8', 'utf-8', 'base64', 'hex', 'ascii',
  'get', 'post', 'put', 'patch', 'delete', 'all', 'any', 'never', 'always',
]);

const NUMERIC_RE = /^-?\d+(\.\d+)?$/;
const VERSION_RE = /^v?\d+\.\d+(\.\d+)?([-+][\w.]+)?$/;
const LETTERS_SPACES_RE = /^[A-Za-z ]+$/;

/** Shannon entropy in bits per character. */
export function entropyBitsPerChar(s: string): number {
  if (s.length === 0) return 0;
  const counts = new Map<string, number>();
  for (const ch of s) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  let h = 0;
  for (const c of counts.values()) {
    const p = c / s.length;
    h -= p * Math.log2(p);
  }
  return h;
}

/**
 * The ladder. Evaluated in order; the first rung that fires owns the verdict.
 *
 * Note rung 6's escape hatch. `JWT_SECRET=s3cr3t` is 6 characters and `SMTP_PASSWORD=hunter2` is 7 —
 * both are genuine credentials that a flat length floor would discard. The key NAME is what saves
 * them.
 */
export function gate(key: string, value: string): GateVerdict {
  const keyLooksSecret = SECRET_KEY_RE.test(key);
  const keyLooksPublic = PUBLIC_PREFIX_RE.test(key);

  if (value.length === 0) {
    return { kind: 'skip', rung: 1, reason: 'empty — there is nothing to search for' };
  }
  if (DENY_VALUES.has(value.toLowerCase())) {
    return {
      kind: 'skip',
      rung: 2,
      reason: `the value "${value}" is a common word or setting — searching for it matches thousands of times in ordinary code`,
    };
  }
  if (NUMERIC_RE.test(value)) {
    return { kind: 'skip', rung: 3, reason: 'purely numeric — "1" alone matched 37,781 times in 5 MB of ordinary bundles' };
  }
  if (VERSION_RE.test(value)) {
    return { kind: 'skip', rung: 4, reason: 'looks like a version number' };
  }
  if (value.length < 6) {
    return { kind: 'skip', rung: 5, reason: `only ${value.length} characters — below 6 the hits are unbounded and unrankable` };
  }
  if (value.length < 12 && !keyLooksSecret) {
    return {
      kind: 'skip',
      rung: 6,
      reason: `${value.length} characters and the variable name carries no secret-shaped word (KEY, SECRET, TOKEN, PASSWORD…)`,
    };
  }
  if (LETTERS_SPACES_RE.test(value) && value.length < 16 && !keyLooksSecret) {
    return {
      kind: 'skip',
      rung: 7,
      reason: 'letters and spaces only — dictionary-shaped values match ordinary code, which is what actually predicts noise',
    };
  }
  if (entropyBitsPerChar(value) < 2.0 && value.length < 20 && !keyLooksSecret) {
    return { kind: 'skip', rung: 8, reason: 'very low character variety — this looks like padding or a placeholder' };
  }

  if (keyLooksPublic && keyLooksSecret) return { kind: 'search', classification: 'search-conflict' };
  if (keyLooksPublic) return { kind: 'search', classification: 'search-public' };
  return { kind: 'search', classification: 'search' };
}

/**
 * THE IRREDUCIBLE CASE, stated so nobody "fixes" it later.
 *
 * `ADMIN_PASSWORD=admin` is a real credential whose value sits on the deny-list. Rung 2 drops it.
 *
 * The tempting fix — let the key-name signal override the deny-list — reintroduces `DEBUG_TOKEN=true`
 * and every other config variable whose name happens to contain TOKEN, and the noise floor comes
 * straight back. So the value is NOT searched by default; instead it is routed to a visible
 * "Secret-shaped, but not searchable" panel with its exact reason and a one-click "search anyway"
 * that runs the real search and shows the real count.
 */
export function isSecretShapedButSkipped(key: string, verdict: GateVerdict): boolean {
  return verdict.kind === 'skip' && SECRET_KEY_RE.test(key);
}

/**
 * Post-hoc rung 9, and it is load-bearing.
 *
 * Do NOT try to predict a needle's hit count from N/A^L. That model is off by up to NINE orders of
 * magnitude for dictionary-shaped values — `function` at length 8 predicts 1.9e-6 expected hits and
 * actually gets 9,303. Aho–Corasick is flat-cost regardless of pattern count, so the honest move is
 * to run the search and demote afterwards with the real number on screen.
 */
export const OVERCOMMON_THRESHOLD = 50;

export function isOvercommon(hitCount: number): boolean {
  return hitCount > OVERCOMMON_THRESHOLD;
}
